<template>
  <div>
    <div class="category-title">
      <h1 class="h1-like">
        {{ $t("dashboard.google_looker_studio", "Google Looker Studio") }}
      </h1>
    </div>

    <b-skeleton-wrapper :loading="ui.loading">
      <template #loading>
        <b-card>
          <b-skeleton width="85%"></b-skeleton>
          <b-skeleton width="55%"></b-skeleton>
          <b-skeleton width="70%"></b-skeleton>
        </b-card>
      </template>

      <b-card>
        <h3 class="mb-4">
          {{
            $t(
              "reports.google_looker_studio_description",
              "Select a report to generate a Google Looker Studio report."
            )
          }}
        </h3>

        <b-card
          v-for="report in configuration.report_list"
          :key="report.id"
          :title="report.name"
          :img-src="report.preview_image_url"
          img-alt="Report preview"
          img-top
          style="max-width: 50%"
          class="mb-2"
          :class="{ 'active-report': form.report_id === report.id }"
        >
          <div class="tw-w-full text-center">
            <b-button
              pill
              @click="toogleReportSelection(report.id)"
              class="mx-auto"
              :variant="form.report_id === report.id ? 'secondary' : 'success'"
            >
              {{
                form.report_id === report.id
                  ? $t("reports.deselect_report", "Deselect Report")
                  : $t("reports.select_report", "Select Report")
              }}
            </b-button>
          </div>
        </b-card>

        <b-alert
          v-if="ui.errors.length"
          variant="danger"
          class="mt-3"
          dismissible
          :show="ui.errors.length > 0"
          @dismissed="ui.errors = []"
        >
          <ul class="mb-0">
            <li v-for="(error, index) in ui.errors" :key="index">
              <b-icon icon="exclamation-triangle-fill" class="me-1" />
              {{ error.code || $t("errors.error", "Error") }}:
              {{ error.message }}
              <br />
              <small v-if="error.field">
                {{ $t("errors.details", "Details") }}: {{ error.field }}
              </small>
            </li>
          </ul>
        </b-alert>

        <b-collapse
          :visible="form.report_id.length > 0"
          class="mt-3"
          :disabled="response.report_url"
        >
          <h3>
            {{ $t("reports.generate_report_url_title", "Generate Report URL") }}
          </h3>
          <h4>
            {{
              $t(
                "reports.generate_report_url_description",
                "Enter your Google email and select the campaigns you want to export."
              )
            }}
          </h4>

          <b-form-group
            :label="$t('reports.user_email_label', 'Google Email')"
            class="form-unit--input"
          >
            <b-form-input
              v-model="form.user_email"
              type="email"
              :placeholder="$t('reports.user_email_placeholder', 'Enter your google email')"
            />
          </b-form-group>

          <b-form-group
            :label="$t('reports.slect_export_campaign', 'Select Campaign for report')"
            class="form-unit--multiselect tw-w-full"
          >
            <SelectCampaign
              v-model="form.export_campaign_list"
              default-styles
              general
              :multiple="true"
              :taggable="true"
              :allow-empty="true"
              track-by="id"
              :per-page="10"
            />
          </b-form-group>

          <div class="text-center tw-py-6 tw-my-4">
            <b-button
              v-if="!response.report_url"
              pill
              class="bttn--orange"
              :disabled="!form.report_id || !form.user_email || ui.generating"
              @click="fetchGenerateReportURL"
            >
              <b-icon v-if="!ui.generating" icon="file-earmark-text" class="me-1" />
              <b-spinner small v-if="ui.generating" />
              {{
                ui.generating
                  ? $t("reports.generating_report", "Generating Report...")
                  : $t("reports.generate_report", "Generate Report")
              }}
            </b-button>

            <div>
              <b-alert
                v-if="response.report_url"
                variant="success"
                class="mt-3"
                dismissible
                :show="response.report_url.length > 0"
                @dismissed="reset"
              >
                <b-icon icon="check-circle-fill" class="me-1" />
                {{
                  $t(
                    "reports.report_generated",
                    "Report generated successfully! You can view it below."
                  )
                }}
              </b-alert>

              <b-button
                v-if="response.report_url"
                pill
                variant="success"
                :href="response.report_url"
                target="_blank"
              >
                <b-icon icon="file-earmark-text" class="me-1" />
                {{ $t("reports.view_report", "View Report") }}
              </b-button>
            </div>
          </div>
        </b-collapse>
      </b-card>
    </b-skeleton-wrapper>
  </div>
</template>

<script>
import { defineComponent } from "vue";
import { useGoogleLookerStudioAPI } from "./use-api";
import { mapGetters, mapState } from "vuex";
import SelectCampaign from "@/views/dashboard/subcomponents/selectCampaign.vue";

const googleLookerStudioApi = useGoogleLookerStudioAPI();

export default defineComponent({
  name: "GoogleLookerStudioPage",
  components: {
    SelectCampaign,
  },
  data() {
    return {
      configuration: {
        /**
         * @type {Array<{ id: string, name: string, preview_image_url: string }>}
         */
        report_list: [],
      },
      form: {
        report_id: "",
        user_email: "",
        export_campaign_list: [],
      },
      response: {},
      ui: {
        generating: false,
        loading: false,
        errors: [],
      },
    };
  },
  computed: {
    ...mapState({
      orgEmail: (state) => state.account.account.data.attributes.email,
    }),
    ...mapGetters({
      organization: "getCurrentOrganization",
    }),
    oid() {
      return this.organization.id;
    },
  },
  mounted() {
    this.fetchConfiguration();
  },
  methods: {
    toogleReportSelection(reportId) {
      if (this.form.report_id === reportId) {
        this.form.report_id = "";
        this.form.user_email = "";
      } else {
        this.form.report_id = reportId;
        this.form.user_email = this.orgEmail || "";
      }
    },
    reset() {
      this.form = {
        report_id: "",
        user_email: "",
        export_campaign_list: [],
      };
      this.response = {};
      this.ui.errors = [];
    },
    fetchConfiguration() {
      this.ui.errors = [];
      this.ui.loading = true;

      googleLookerStudioApi
        .fetchConfiguration(this.oid)
        .then(({ data }) => {
          this.configuration = data;
          this.reset();
        })
        .catch(({ errors }) => {
          if (errors && errors.length) {
            this.ui.errors = errors;
          } else {
            this.ui.errors = [
              {
                message: this.$t("errors.fetch_configuration", "Failed to fetch configuration."),
              },
            ];
          }
        })
        .finally(() => {
          this.ui.loading = false;
        });
    },
    fetchGenerateReportURL() {
      this.ui.errors = [];
      this.ui.generating = true;

      const payload = {
        report_id: this.form.report_id,
        user_email: this.form.user_email,
        export_campaign_list: this.form.export_campaign_list.map((c) => Number(c.id)),
      };

      googleLookerStudioApi
        .fetchGenerateReportURL(this.oid, payload)
        .then(({ data }) => {
          this.response = data;
        })
        .catch(({ errors }) => {
          if (errors && errors.length) {
            this.ui.errors = errors;
          } else {
            this.ui.errors = [
              {
                message: this.$t("errors.generate_report", "Failed to generate report."),
              },
            ];
          }
        })
        .finally(() => {
          this.ui.generating = false;
        });
    },
  },
});
</script>

<style lang="scss" scoped>
.active-report {
  @apply tw-bg-green-100 tw-border-lime-500;
}
</style>
